import moment from 'moment-timezone';
import * as Cookies from 'js-cookie';
import { Component, Input, OnInit, OnChanges, OnDestroy, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConstantsService } from '../../services/constants/constants.service';
import {
  PulseAiSummaryService,
  FeedbackSection,
  FeedbackValue,
  PulseAISummaryFilters,
  PulseAISummaryAvailabilityResponse,
  AIResultStatus,
} from '../../services/pulse-ai-summary/pulse-ai-summary.service';
import { AppsyncService } from '../../services/appsync/appsync.service';

export enum SummaryState {
  PRE_GENERATION = 'pre-generation',
  LOADING = 'loading',
  GENERATED = 'generated',
  UNAVAILABLE = 'unavailable',
  PARTIAL = 'partial',
}

export enum FeedbackType {
  Positive = 'positive',
  Negative = 'negative',
  Neutral = 'neutral',
}

export interface AreaItem {
  title: string;
  description: string;
}

export interface OrganisationalSummary {
  summary: string;
  responseCount: number;
  questionCount: number;
  areasOfStrength: AreaItem[];
  areasOfImprovement: AreaItem[];
}

export interface FeedbackCommentsSummary {
  paragraphs: string[];
  feedbackCount: number;
}

export interface CheckAvailabilityOptions {
  onNoData?: () => void;
  onError?: () => void;
}

@Component({
  selector: 'app-pulse-ai-summary',
  templateUrl: './pulse-ai-summary.component.pug',
  styleUrls: ['./pulse-ai-summary.component.scss'],
  standalone: false,
})
export class PulseAiSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() start: any;
  @Input() end: any;
  @Input() request: any[] = [];
  @Input() survey: any[] = [];
  @Input() department: any[] = [];
  @Input() hashId: string = '';

  @Output() userFeedback = new EventEmitter<{ section: string; feedback: FeedbackType }>();
  @Output() summariseClicked = new EventEmitter<void>();
  @Output() canSummariseChange = new EventEmitter<boolean>();

  SummaryState = SummaryState;
  FeedbackType = FeedbackType;

  overallState: SummaryState = SummaryState.PRE_GENERATION;
  orgSummaryState: SummaryState = SummaryState.PRE_GENERATION;
  feedbackSummaryState: SummaryState = SummaryState.PRE_GENERATION;

  canSummarise: boolean = false;
  canSummariseOrgPerformance: boolean = false;
  canSummariseComments: boolean = false;
  isCheckingAvailability: boolean = false;
  pulseAiResultId: number | undefined;

  orgFeedback: FeedbackType = FeedbackType.Neutral;
  feedbackCommentsFeedback: FeedbackType = FeedbackType.Neutral;

  organisationalSummary: OrganisationalSummary = {
    summary: '',
    responseCount: 0,
    questionCount: 0,
    areasOfStrength: [],
    areasOfImprovement: [],
  };

  feedbackCommentsSummary: FeedbackCommentsSummary = {
    paragraphs: [],
    feedbackCount: 0,
  };

  loadingMessage = $localize`Generating your AI summary`;
  loadingMessages: string[] = [
    $localize`Processing feedback comments...`,
    $localize`Highlighting what matters...`,
    $localize`Building your insights...`,
    $localize`Preparing your summary...`,
    $localize`Applying the finishing touches...`,
  ];
  currentMessageIndex: number = 0;
  isMessageTransitioning: boolean = false;
  messageRotationInterval: ReturnType<typeof setInterval> | null = null;

  disabledTooltip = $localize`Not enough data to generate an AI summary for the chosen filters.`;
  helpfulTooltip = $localize`Helpful`;
  notHelpfulTooltip = $localize`Not helpful`;

  destroy$ = new Subject<void>();

  constructor(
    private pulseAiSummaryService: PulseAiSummaryService,
    private appsyncService: AppsyncService,
  ) {}

  ngOnInit(): void {
    this.checkAvailability();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const filterInputs = ['start', 'end', 'request', 'survey', 'department', 'hashId'];
    const hasFilterChange = filterInputs.some((input) => changes[input] && !changes[input].firstChange);

    if (hasFilterChange) {
      if (changes['hashId']?.currentValue) {
        this.hashId = changes['hashId'].currentValue;
      }
      this.closeAppSyncConnection();
      this.resetStates();
      this.checkAvailability();
    }
  }

  ngOnDestroy(): void {
    this.closeAppSyncConnection();
    this.stopMessageRotation();
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkAvailability(options: CheckAvailabilityOptions = {}): void {
    if (!this.hashId) {
      this.hashId = this.generateHashId();
    }

    if (!this.hashId) {
      this.canSummarise = false;
      return;
    }

    const { onNoData, onError } = options;
    this.isCheckingAvailability = true;
    const filters = this.buildFiltersPayload();

    this.pulseAiSummaryService
      .checkAvailability('pulse', this.hashId, filters || {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isCheckingAvailability = false;
          this.handleAvailabilityResponse(response);
          if (onNoData && this.overallState !== SummaryState.GENERATED && this.overallState !== SummaryState.PARTIAL) {
            onNoData();
          }
        },
        error: () => {
          if (onError) onError();
          this.isCheckingAvailability = false;
          this.canSummarise = false;
          this.canSummariseChange.emit(false);
        },
      });
  }

  handleAvailabilityResponse(response: PulseAISummaryAvailabilityResponse): void {
    const data = response.data;

    this.canSummariseOrgPerformance = data.summarise_org_performance;
    this.canSummariseComments = data.summarise_comments;
    this.canSummarise = this.canSummariseOrgPerformance || this.canSummariseComments;
    this.canSummariseChange.emit(this.canSummarise);

    this.pulseAiResultId = data.pulse_ai_result_id ?? undefined;

    const orgPerfStatus = data.org_perf_summary?.status || '';
    const feedbackStatus = data.feedback_comments_summary?.status || '';

    this.mapApiResponseToData(data);

    this.updateStatesFromStatus(orgPerfStatus, feedbackStatus);

    // Fetch existing feedback status if summary is generated
    if (this.pulseAiResultId && (orgPerfStatus === 'completed' || feedbackStatus === 'completed')) {
      this.fetchFeedbackStatus();
    }
  }

  mapApiResponseToData(data: PulseAISummaryAvailabilityResponse['data']): void {
    this.organisationalSummary = {
      summary: data.org_perf_summary?.summary || '',
      responseCount: data.org_perf_summary?.response_count || 0,
      questionCount: data.org_perf_summary?.question_count || 0,
      areasOfStrength: data.area_of_strength?.items || [],
      areasOfImprovement: data.area_of_improvement?.items || [],
    };

    const feedbackText = data.feedback_comments_summary?.summary || '';
    let feedbackParagraphs: string[] = [];
    if (feedbackText.trim()) {
      feedbackParagraphs = feedbackText.split(/\n\n+/).filter((p) => p.trim());
      if (feedbackParagraphs.length === 0) {
        feedbackParagraphs = [feedbackText];
      }
    }

    this.feedbackCommentsSummary = {
      paragraphs: feedbackParagraphs,
      feedbackCount: data.feedback_comments_summary?.feedback_count || 0,
    };
  }

  updateStatesFromStatus(orgPerfStatus: AIResultStatus, feedbackStatus: AIResultStatus): void {
    if (orgPerfStatus === 'in_progress') {
      this.orgSummaryState = SummaryState.LOADING;
    } else if (orgPerfStatus === 'completed') {
      const hasOrgContent = this.organisationalSummary.summary?.trim();
      this.orgSummaryState = hasOrgContent ? SummaryState.GENERATED : SummaryState.UNAVAILABLE;
    } else if (orgPerfStatus === 'failed') {
      this.orgSummaryState = SummaryState.UNAVAILABLE;
    } else {
      this.orgSummaryState = this.canSummariseOrgPerformance ? SummaryState.PRE_GENERATION : SummaryState.UNAVAILABLE;
    }

    if (feedbackStatus === 'in_progress') {
      this.feedbackSummaryState = SummaryState.LOADING;
    } else if (feedbackStatus === 'completed') {
      const hasFeedbackContent = this.feedbackCommentsSummary.paragraphs?.length > 0;
      this.feedbackSummaryState = hasFeedbackContent ? SummaryState.GENERATED : SummaryState.UNAVAILABLE;
    } else if (feedbackStatus === 'failed') {
      this.feedbackSummaryState = SummaryState.UNAVAILABLE;
    } else {
      this.feedbackSummaryState = this.canSummariseComments ? SummaryState.PRE_GENERATION : SummaryState.UNAVAILABLE;
    }

    if (this.orgSummaryState === SummaryState.LOADING || this.feedbackSummaryState === SummaryState.LOADING) {
      this.overallState = SummaryState.LOADING;
    } else if (
      this.orgSummaryState === SummaryState.GENERATED &&
      this.feedbackSummaryState === SummaryState.GENERATED
    ) {
      this.overallState = SummaryState.GENERATED;
      this.stopMessageRotation();
    } else if (
      this.orgSummaryState === SummaryState.GENERATED ||
      this.feedbackSummaryState === SummaryState.GENERATED
    ) {
      this.overallState = SummaryState.PARTIAL;
      this.stopMessageRotation();
    } else if (
      this.orgSummaryState === SummaryState.UNAVAILABLE &&
      this.feedbackSummaryState === SummaryState.UNAVAILABLE
    ) {
      this.overallState = (orgPerfStatus || feedbackStatus)
        ? SummaryState.UNAVAILABLE
        : SummaryState.PRE_GENERATION;
      this.stopMessageRotation();
    } else if (
      this.orgSummaryState === SummaryState.PRE_GENERATION &&
      this.feedbackSummaryState === SummaryState.PRE_GENERATION
    ) {
      this.overallState = SummaryState.PRE_GENERATION;
    } else if (
      (this.orgSummaryState === SummaryState.PRE_GENERATION &&
        this.feedbackSummaryState === SummaryState.UNAVAILABLE) ||
      (this.orgSummaryState === SummaryState.UNAVAILABLE && this.feedbackSummaryState === SummaryState.PRE_GENERATION)
    ) {
      this.overallState = SummaryState.PRE_GENERATION;
    }
  }

  resetStates(): void {
    this.stopMessageRotation();
    this.overallState = SummaryState.PRE_GENERATION;
    this.orgSummaryState = SummaryState.PRE_GENERATION;
    this.feedbackSummaryState = SummaryState.PRE_GENERATION;
    this.orgFeedback = FeedbackType.Neutral;
    this.feedbackCommentsFeedback = FeedbackType.Neutral;
    this.pulseAiResultId = undefined;
    this.organisationalSummary = {
      summary: '',
      responseCount: 0,
      questionCount: 0,
      areasOfStrength: [],
      areasOfImprovement: [],
    };
    this.feedbackCommentsSummary = {
      paragraphs: [],
      feedbackCount: 0,
    };
  }

  onSummariseClick(): void {
    if (!this.canSummarise) {
      return;
    }
    this.summariseClicked.emit();
    // Check if data already exists before triggering generation
    this.checkAvailability({
      onNoData: () => this.triggerGeneration(),
      onError: () => this.triggerGeneration(),
    });
  }

  triggerGeneration(): void {
    this.overallState = SummaryState.LOADING;
    this.orgSummaryState = SummaryState.LOADING;
    this.feedbackSummaryState = SummaryState.LOADING;
    this.startMessageRotation();

    this.subscribeToAppSync();

    const filters = this.buildFiltersPayload();

    this.pulseAiSummaryService
      .generateSummary('pulse', this.hashId, filters || {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.checkAvailability({
            onNoData: () => {},
            onError: () => {},
          });
        },
        error: () => {
          this.closeAppSyncConnection();
          this.setStateUnavailable();
        },
      });
  }

  generateHashId(): string {
    // Get user UID from a cookie
    const userUid = Cookies.get('user_uid') || '';

    // Generate hash from filters only
    const filterData = {
      request: this.request || [],
      survey: this.survey || [],
      department: this.department || [],
      start: this.start ? this.start.format?.('YYYY-MM-DD') || String(this.start) : '',
      end: this.end ? this.end.format?.('YYYY-MM-DD') || String(this.end) : '',
    };

    const str = JSON.stringify(filterData);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    const filterHash = Math.abs(hash).toString(16);

    // Return format: user_uid-filter_hash
    return userUid ? `${userUid}-${filterHash}` : filterHash;
  }

  handleOrgFeedbackClick(feedback: FeedbackType): void {
    if (this.orgFeedback !== FeedbackType.Neutral) {
      return;
    }
    this.orgFeedback = feedback;
    this.userFeedback.emit({ section: 'organisational', feedback });
    this.submitFeedbackToApi('organisational_performance', feedback);
  }

  handleFeedbackCommentsFeedbackClick(feedback: FeedbackType): void {
    if (this.feedbackCommentsFeedback !== FeedbackType.Neutral) {
      return;
    }
    this.feedbackCommentsFeedback = feedback;
    this.userFeedback.emit({ section: 'feedbackComments', feedback });
    this.submitFeedbackToApi('feedback_comments', feedback);
  }

  /**
   * Submits feedback to the API for this AI summary.
   *
   * Design Note: Only one feedback is allowed per user per pulse_ai_result_id.
   * The section parameter is stored as metadata but doesn't create separate feedback
   * entries. Once feedback is submitted for any section, subsequent submissions
   * will receive a 409 Conflict response from the backend.
   */
  submitFeedbackToApi(section: FeedbackSection, feedback: FeedbackType): void {
    if (feedback === FeedbackType.Neutral) {
      return;
    }

    if (!this.hashId || !this.pulseAiResultId) {
      return;
    }

    let feedbackValue: FeedbackValue;
    if (feedback === FeedbackType.Positive) {
      feedbackValue = 'positive';
    } else {
      feedbackValue = 'negative';
    }

    const filters = this.buildFiltersPayload();

    this.pulseAiSummaryService
      .submitFeedback(section, feedbackValue, this.hashId, this.pulseAiResultId, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => {},
      });
  }


  fetchFeedbackStatus(): void {
    if (!this.pulseAiResultId) {
      return;
    }

    this.pulseAiSummaryService
      .getFeedbackStatus(this.pulseAiResultId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const feedbackValue = response.data?.feedback;
          // Single feedback applies to both sections (single-source-of-truth design)
          if (feedbackValue === 'positive') {
            this.orgFeedback = FeedbackType.Positive;
            this.feedbackCommentsFeedback = FeedbackType.Positive;
          } else if (feedbackValue === 'negative') {
            this.orgFeedback = FeedbackType.Negative;
            this.feedbackCommentsFeedback = FeedbackType.Negative;
          }
        },
        error: () => {},
      });
  }

  buildFiltersPayload(): PulseAISummaryFilters | undefined {
    const filters: PulseAISummaryFilters = {};

    if (this.request && this.request.length > 0) {
      filters.request = this.request;
    }
    if (this.survey && this.survey.length > 0) {
      filters.questionnaire = this.survey;
    }
    if (this.department && this.department.length > 0) {
      filters.department = this.department;
    }
    if (this.start) {
      filters.start = this.start.format?.('DD-MMM-YYYY') || String(this.start);
    }
    if (this.end) {
      filters.end = this.end.format?.('DD-MMM-YYYY') || String(this.end);
    }

    const tz = moment.tz.guess();
    if (tz) {
      filters.tz = tz;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  isOrgFeedbackSubmitted(): boolean {
    return this.orgFeedback !== FeedbackType.Neutral;
  }

  isFeedbackCommentsFeedbackSubmitted(): boolean {
    return this.feedbackCommentsFeedback !== FeedbackType.Neutral;
  }

  setStateGenerated(): void {
    this.overallState = SummaryState.GENERATED;
    this.orgSummaryState = SummaryState.GENERATED;
    this.feedbackSummaryState = SummaryState.GENERATED;
  }

  setStateUnavailable(): void {
    this.stopMessageRotation();
    this.overallState = SummaryState.UNAVAILABLE;
    this.orgSummaryState = SummaryState.UNAVAILABLE;
    this.feedbackSummaryState = SummaryState.UNAVAILABLE;
  }

  setStatePartialOrgOnly(): void {
    this.overallState = SummaryState.PARTIAL;
    this.orgSummaryState = SummaryState.GENERATED;
    this.feedbackSummaryState = SummaryState.UNAVAILABLE;
  }

  setStatePartialFeedbackOnly(): void {
    this.overallState = SummaryState.PARTIAL;
    this.orgSummaryState = SummaryState.UNAVAILABLE;
    this.feedbackSummaryState = SummaryState.GENERATED;
  }

  getMetricIcon(title: string, type: 'strength' | 'improvement'): string {
    const metricsMap = ConstantsService.METRICS_ICON_MAP;
    const metricKey = Object.keys(metricsMap).find((key) => key.toLowerCase() === title.toLowerCase());

    if (metricKey && metricsMap[metricKey]) {
      const metric = metricsMap[metricKey];
      if (type === 'strength') {
        return metric.metric_without_background_green_icon || metric.metric_green_icon;
      } else {
        return metric.metric_without_background_red_icon || metric.metric_red_icon;
      }
    }

    if (type === 'strength') {
      return 'https://assets.xref.com/omni/shared/thumbs_up.svg';
    }
    return 'https://assets.xref.com/omni/shared/thumbs_down.svg';
  }

  closeAppSyncConnection(): void {
    this.appsyncService.closeConnection();
  }

  // Check availability after receiving a "published" status from AppSync
  checkAvailabilityAfterPublish(): void {
    this.checkAvailability({
      onNoData: () => this.setStateUnavailable(),
      onError: () => this.setStateUnavailable(),
    });
  }

  // Subscribe to the AppSync channel to receive notification when the summary is published
  subscribeToAppSync(): void {
    if (!this.hashId) {
      this.setStateUnavailable();
      return;
    }

    // Close any existing connection before creating a new one
    // This prevents multiple subscriptions from being active simultaneously
    this.closeAppSyncConnection();

    // Subscribe to the AppSync channel with callback for when status is "published"
    this.appsyncService
      .subscribeToChannel(ConstantsService.PULSE_AI_SUMMARY_CHANNEL_NAMESPACE, this.hashId, (event) => {
        // When status is "published", check availability to get the results
        this.checkAvailabilityAfterPublish();
      })
      .catch((error) => {
        this.setStateUnavailable();
      });
  }

  // Check if the summary is already available, if not, then subscribe to AppSync
  // This handles the scenario where the summary was published while the subscriber wasn't listening
  checkAvailabilityAndSubscribe(): void {
    this.checkAvailability({
      onNoData: () => this.subscribeToAppSync(),
      onError: () => this.subscribeToAppSync(),
    });
  }

  startMessageRotation(): void {
    this.currentMessageIndex = 0;
    this.isMessageTransitioning = false;

    if (this.messageRotationInterval) {
      clearInterval(this.messageRotationInterval);
    }

    this.messageRotationInterval = setInterval(() => {
      this.nextMessage();
    }, 3000);
  }

  stopMessageRotation(): void {
    if (this.messageRotationInterval) {
      clearInterval(this.messageRotationInterval);
      this.messageRotationInterval = null;
    }
    this.currentMessageIndex = 0;
    this.isMessageTransitioning = false;
  }

  nextMessage(): void {
    this.isMessageTransitioning = true;

    setTimeout(() => {
      this.currentMessageIndex = (this.currentMessageIndex + 1) % this.loadingMessages.length;
      this.isMessageTransitioning = false;
    }, 400);
  }
}
